import * as React from "react";

import { cn } from "@/lib/utils";

export interface ExpandableTextProps {
  /** The (often AI-generated) text to display. */
  children: React.ReactNode;
  /** Number of lines to show before clamping. Default 5. */
  collapsedLines?: number;
  /** Label for the expand action. Default "Ver mais". */
  expandLabel?: string;
  /** Label for the collapse action. Default "Ver menos". */
  collapseLabel?: string;
  className?: string;
}

/**
 * Read-only text block that auto-fits its content. When the text is taller than
 * `collapsedLines`, it clamps and shows a text-only "Ver mais"/"Ver menos"
 * toggle (no icons, per Comp UI conventions) instead of a fixed scroll box.
 */
export function ExpandableText({
  children,
  collapsedLines = 5,
  expandLabel = "Ver mais",
  collapseLabel = "Ver menos",
  className,
}: ExpandableTextProps) {
  const ref = React.useRef<HTMLParagraphElement | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [clampable, setClampable] = React.useState(false);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setClampable(el.scrollHeight - el.clientHeight > 1);
  }, []);

  // Re-measure whenever the text or expand state changes. `children` and
  // `expanded` are intentional triggers even though `measure` reads the DOM.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on content/expand change
  React.useLayoutEffect(() => {
    measure();
  }, [measure, children, expanded]);

  React.useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  return (
    <div>
      <p
        ref={ref}
        className={cn("whitespace-pre-line", className)}
        style={
          expanded
            ? undefined
            : {
                display: "-webkit-box",
                WebkitLineClamp: collapsedLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
        }
      >
        {children}
      </p>
      {(clampable || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {expanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
}
